<template>
  <Modal
    :open="localShowModal"
    footer-alignment="center"
    header-alignment="center"
    :with-close-button="true"
    :with-expand-button="false"
    :title="text.title"
    @update:open="onUpdateOpen"
  >
    <div class="mx-4 md:mx-20 my-5 text-sm">
      <p class="mb-3">{{ text.intro }}</p>
      <p class="mb-1">{{ text.usesIntro }}</p>
      <ul class="list-disc pl-6 mb-3">
        <li v-for="item in text.uses" :key="item">{{ item }}</li>
      </ul>
      <p class="mb-3">{{ text.noOtherUses }}</p>
      <p class="mb-3">{{ text.noConsent }}</p>
      <p>
        {{ text.moreInfoBefore }}<a :href="policyLink" class="underline text-soft-blue-700 font-bold" target="_blank">{{ text.moreInfoLink }}</a>{{ text.moreInfoAfter }}
      </p>
    </div>
    <template #footer>
      <CGButton @click="acknowledge">{{ text.button }}</CGButton>
    </template>
  </Modal>
</template>
<script>
import { defineComponent } from 'vue';

// Notice is shown until this date (release + 6 months, Rome time); the acknowledgement cookie expires at the same time
const END_DATE = new Date('2027-04-30T23:59:59+02:00');
const COOKIE_PREFIX = 'cg_pixel_notice_';

// Texts provided by Compliance: shown as-is, not translated through i18n
const TEXTS = {
  it: {
    title: 'Utilizzo dei pixel di tracciamento',
    intro: 'Ti informiamo che Cyber Guru utilizza pixel di tracciamento in alcune email inviate dalla piattaforma per rilevarne l’apertura.',
    usesIntro: 'I pixel sono utilizzati esclusivamente per:',
    uses: [
      'reimpostazione della password, per finalità di sicurezza;',
      'simulazioni di phishing, per rilevare l’esito della simulazione e supportare il percorso formativo;',
      'promemoria formativi, per verificarne la ricezione.'
    ],
    noOtherUses: 'I pixel non sono utilizzati per finalità pubblicitarie, per valutare il rendimento lavorativo o per finalità disciplinari.',
    noConsent: 'Per questi utilizzi non è richiesto il tuo consenso, in quanto rientrano nei casi di esenzione previsti dalla normativa applicabile.',
    moreInfoBefore: 'Per maggiori informazioni, consulta l’',
    moreInfoLink: 'informativa privacy',
    moreInfoAfter: ' disponibile in piattaforma.',
    button: 'Ho capito'
  },
  en: {
    title: 'Use of Tracking Pixels',
    intro: 'Please note that Cyber Guru uses tracking pixels in certain emails sent through the platform to detect when they are opened.',
    usesIntro: 'Tracking pixels are used exclusively for:',
    uses: [
      'password resets, for security purposes;',
      'phishing simulations, to determine the outcome of the simulation and support your training journey;',
      'training reminders, to verify their receipt.'
    ],
    noOtherUses: 'Tracking pixels are not used for advertising purposes, to assess your work performance, or for disciplinary purposes.',
    noConsent: 'Your consent is not required for these uses, as they fall within the exemptions provided for under applicable data protection law.',
    moreInfoBefore: 'For more information, please refer to the ',
    moreInfoLink: 'privacy notice',
    moreInfoAfter: ' available on the platform.',
    button: 'Got it'
  }
};

export default defineComponent({
  data() {
    return {
      localShowModal: false,
      policyLink: null,
      text: TEXTS.en
    }
  },

  mounted() {
    // Any error here simply leaves the notice hidden
    try {
      if (!this.$profile || !this.$user?.id || this.$impersonating) return;
      if (new Date() > END_DATE || this.isAcknowledged()) return;

      // New users get the updated policy in PolicyModal: mark as seen so the notice is not shown after its reload
      if (!this.$profile.policy_agreed) {
        this.setAcknowledged();
        return;
      }

      // Without a profile language the page falls back to the company language: the notice does the same
      const lang = this.$profile.language || this.$company?.default_language;
      this.text = lang === 'it' ? TEXTS.it : TEXTS.en;
      this.policyLink = this.getPolicyLink(lang);
      this.localShowModal = true;
    } catch (e) {
      console.error('Privacy notice not shown =>>', e);
    }
  },

  methods: {
    cookieName() {
      return `${COOKIE_PREFIX}${this.$user.id}`;
    },

    isAcknowledged() {
      return document.cookie.split('; ').some(c => c.startsWith(`${this.cookieName()}=`));
    },

    setAcknowledged() {
      const secure = window.location.protocol === 'https:' ? '; Secure' : '';
      document.cookie = `${this.cookieName()}=1; expires=${END_DATE.toUTCString()}; path=/; SameSite=Lax${secure}`;
    },

    // Company policy when uploaded (profile language, then company language, then any uploaded one),
    // otherwise the default policy in the same language as the notice
    getPolicyLink(lang) {
      const profileLang = this.$profile.language;
      const compLang = this.$company.default_language;
      const defaultLang = lang === 'it' ? 'it' : 'en';
      const privacyPolicyFiles = this.$company.file_status?.['privacypolicy'] || {};
      const policyLang = [profileLang, compLang, ...Object.keys(privacyPolicyFiles)].find(l => l && privacyPolicyFiles[l]);
      if (policyLang) {
        return `/common/company/${this.$user.company_id}/${policyLang}/privacypolicy.pdf`;
      }
      return `/common/default/${defaultLang}/privacypolicy.pdf`;
    },

    acknowledge() {
      try {
        this.setAcknowledged();
      } catch (e) {
        console.error('Privacy notice acknowledgement not saved =>>', e);
      }
      this.localShowModal = false;
    },

    onUpdateOpen(isOpen) {
      if (!isOpen) this.acknowledge();
    }
  }
});
</script>
